import { useLiveQuery } from "dexie-react-hooks";
import { FormEvent, useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { db } from "../../../db/db";
import { useForm } from "../../../hooks/form-hook";
import { InputsType } from "../../../hooks/form-hook-typing";
import { UpdateStoreRequestType } from "../../../models/Stores";
import { updateStore } from "../../../services";
import { VALIDATOR_MAXLENGTH, VALIDATOR_REQUIRE } from "../../../utils/validators";
import InputForwardRef, { InputForwardType } from "../../FormElements/InputForwardRef";
import PrimaryButton from "../../FormElements/PrimaryButton";
import Loading from "../../Loading";
import Modal from "../../UIelements/Modal";
import * as Styled from './styles'

type ModalProps = {
  onCancel: () => void;
  onUpdateList: () => void;
  store: any;
};

enum InputStoreNames {
  NAME = 'storeName'
};

const initialValue = {
  value: '',
  isValid: false,
};

const initialStoreInputs: InputsType = {
  [InputStoreNames.NAME]: initialValue
};

const getSubmitStoreData = (
  inputs: InputsType,
    id: number,
  companyId: number,
): UpdateStoreRequestType => ({
  store: {
    id: id,
    storeName: inputs[InputStoreNames.NAME].value,
    cnpj: '',
    zipCode: 0,
    street: '',
    streetNumber: 0,
    neighborhood: '',
    state: '',
    city: '',
    latitude: 0,
    longitude: 0,
    companyId: companyId
  },
});

const ModalUpdateStore = (props: ModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl()
  const userDb = useLiveQuery(() => db.user.toArray())
  const [formStoreState, inputStoreHandler] = useForm(
    initialStoreInputs,
    false
  );

  const storeNameRef = useRef<InputForwardType>(null);

  const handleUpdateStore = useCallback(
    async (): Promise<boolean> => {
      const data = getSubmitStoreData(
        formStoreState.inputs,
        props.store.id,
        userDb![0].companyId
      );
      try {
        await updateStore(data, userDb![0].accessToken)
          toast.success(
            intl.formatMessage({ id: 'store.grupo de equipamentos editado com sucesso!' }).toUpperCase()
          );
        return true;
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'store.não foi possível editar o grupo. Tente novamente mais tarde',
          }).toUpperCase()
        );
        return false;
      }
    },
    [userDb, formStoreState.inputs, intl, props]

  );

 const handleSubmit = useCallback(
    async (event: FormEvent) => {
      setIsLoading(true);
      event.preventDefault();
      if (!formStoreState.isValid) {
        setIsLoading(false);
        return;
      }
      const isSuccess = await handleUpdateStore();
      setIsLoading(false);
      if (!isSuccess) return;
      props.onUpdateList();
      props.onCancel();
    },
    [
      formStoreState.isValid,
      handleUpdateStore,
      props
    ]
  );

  const loading = <Loading />;

  const footer = (
    <>
      <PrimaryButton
        type='submit'
        size='buttonModal'
        model='confirmation-gradient-modal'
        width='108px'
      >
        { intl.formatMessage({ id: "store.salvar" }) }
      </PrimaryButton>
      <PrimaryButton
        type='button'
        size='buttonModal'
        model='cancellation'
        width='108px'
        onClick={props.onCancel}
      >
        { intl.formatMessage({ id: "menus.CANCELAR" }) }
      </PrimaryButton>
    </>
  );

  const content = (
    <>
      <Styled.Container>
        <Styled.Row>
          <Styled.Content flexGrow={1}>
            <InputForwardRef
               id={InputStoreNames.NAME}
               ref={storeNameRef}
              type='text'
              label={intl.formatMessage({ id: 'store.Nome do grupo' }) + ' *'}
              initialValid={true}
              initialValue={props.store.storeName}
              validators={[VALIDATOR_REQUIRE(), VALIDATOR_MAXLENGTH(22)]}
              maxLength={22} 
              placeholder={intl.formatMessage({
                id: 'store.informe o nome do grupo',
              })}
              errorText={intl.formatMessage({
                id: 'store.preencha o nome do grupo',
              })}
              onInput={inputStoreHandler}
            />
          </Styled.Content>
        </Styled.Row>
    </Styled.Container>
    </>
  );

  return (
  <>
    <Modal
      title={intl.formatMessage({ id: "store.editar grupo de equipamentos" })}
      width="650px"
      children={isLoading ? loading : content}
      footer={footer}
      onCancel={props.onCancel}
      onSubmit={handleSubmit}
    />
  </>
  );
}

export default ModalUpdateStore;